/**
 * CR-6347: Classification section of the reworked PO Lines filter panel
 * (customer view). Groups the part-classification filters — Commodity Group ->
 * Commodity Type (cascade preserved), Part Number and Description — per the
 * supplied diagram. Input types are unchanged from the previous flat panel.
 */
import type { LucideIcon } from 'lucide-react';
import { POLinesFilters, FilterOptions } from '../../types';
import { FilterSection, FIELD_CLS, LABEL_CLS } from '../fieldStyles';

interface ClassificationSectionProps {
  filters: POLinesFilters;
  onFilterChange: (key: string, value: string) => void;
  commodityGroups: FilterOptions['commodity_groups'];
  commodityTypes: FilterOptions['commodity_types'];
  // Clears the now-invalid Commodity Type when the Group changes.
  onCommodityGroupChange: (value: string) => void;
  /** CR-6347: Optional icon for the section header */
  icon?: LucideIcon;
}

export function ClassificationSection({
  filters,
  onFilterChange,
  commodityGroups,
  commodityTypes,
  onCommodityGroupChange,
  icon,
}: ClassificationSectionProps) {
  return (
    <FilterSection title="Classification" icon={icon}>
      {/* Commodity Group */}
      <div>
        <label className={LABEL_CLS}>Commodity Group</label>
        <select
          value={filters.commodityGroup}
          onChange={(e) => onCommodityGroupChange(e.target.value)}
          className={FIELD_CLS}
        >
          <option value="">All Commodity Groups</option>
          {commodityGroups.map((cg) => (
            <option key={cg.id} value={cg.id}>{cg.name}</option>
          ))}
        </select>
      </div>

      {/* Commodity Type (cascaded by Commodity Group) */}
      <div>
        <label className={LABEL_CLS}>Commodity Type</label>
        <select
          value={filters.commodityType}
          onChange={(e) => onFilterChange('commodityType', e.target.value)}
          className={FIELD_CLS}
        >
          <option value="">All Commodity Types</option>
          {commodityTypes
            .filter(
              (ct) =>
                !filters.commodityGroup ||
                ct.commodity_group_id === parseInt(filters.commodityGroup, 10),
            )
            .map((ct) => (
              <option key={ct.id} value={ct.id}>{ct.name}</option>
            ))}
        </select>
      </div>

      {/* Part Number */}
      <div>
        <label className={LABEL_CLS}>Part Number</label>
        <input
          type="text"
          placeholder="Part number..."
          value={filters.partNumber}
          onChange={(e) => onFilterChange('partNumber', e.target.value)}
          className={FIELD_CLS}
        />
      </div>

      {/* Description */}
      <div>
        <label className={LABEL_CLS}>Description</label>
        <input
          type="text"
          placeholder="Description..."
          value={filters.description}
          onChange={(e) => onFilterChange('description', e.target.value)}
          className={FIELD_CLS}
        />
      </div>
    </FilterSection>
  );
}
